import React, { useState } from 'react';
import { useHilContext, useHilQueue } from '@bizfirst/hil-react';
import { hilInboxApiClient } from '@flow-studio/api';
import type { InboxItemRecord, HilEventLogRecord } from '@flow-studio/api';
import type { HilPresentationRequestEnvelope } from '@bizfirst/hil-types';
import { InboxIcon } from '@bizfirst/common-react';
import { useUIStore } from '@flow-studio/store';
import './HilV13TestPage.css';

const BADGE_COLORS: Record<string, string> = {
  approval: '#3b82f6',
  form: '#8b5cf6',
  chat: '#10b981',
  info: '#6b7280',
};

function badgeForActionType(t?: string) {
  if (!t) return 'approval';
  if (t === 'form' || t === 'chat_with_form') return 'form';
  if (t === 'chat') return 'chat';
  return 'approval';
}

// ── Component ─────────────────────────────────────────────────────────────────

export const HilV13TestPage: React.FC = () => {
  const { pushToEngine, engine } = useHilContext();
  const { active, pending, count } = useHilQueue();
  const { theme } = useUIStore();
  const [log, setLog] = useState<string[]>([]);

  // API section state
  const [actorID, setActorID] = useState<string>(
    localStorage.getItem('hilActorID') ??
    (localStorage.getItem('userId') ? `user:${localStorage.getItem('userId')}` : '') ??
    ''
  );
  const [apiItems, setApiItems] = useState<Array<{ item: InboxItemRecord; log: HilEventLogRecord | null }>>([]);
  const [loadingApi, setLoadingApi] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiLoaded, setApiLoaded] = useState(false);

  // Custom JSON state
  const [customJson, setCustomJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const pushLog = (msg: string) =>
    setLog(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev.slice(0, 19)]);

  // ── Load from API ────────────────────────────────────────────────────────────

  const loadFromApi = async () => {
    if (!actorID.trim()) {
      setApiError('Enter an Actor ID first');
      return;
    }
    setLoadingApi(true);
    setApiError(null);
    setApiItems([]);
    try {
      const itemsResp = await hilInboxApiClient.getPendingItems(actorID.trim());
      if (!itemsResp.success) {
        setApiError(itemsResp.error?.message ?? 'Failed to load inbox items');
        return;
      }
      const items = itemsResp.data ?? [];
      if (items.length === 0) {
        setApiError('No pending HIL items found for this actor.');
        setApiLoaded(true);
        return;
      }

      // Fetch event logs in parallel
      const withLogs = await Promise.all(
        items.map(async (item) => {
          try {
            const logResp = await hilInboxApiClient.getEventLogBySession(item.EngageSessionID);
            return { item, log: logResp.data?.[0] ?? null };
          } catch {
            return { item, log: null };
          }
        })
      );

      setApiItems(withLogs);
      setApiLoaded(true);
      pushLog(`Loaded ${withLogs.length} pending item(s) for actor "${actorID.trim()}"`);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoadingApi(false);
    }
  };

  const injectApiItem = ({ item, log: eventLog }: { item: InboxItemRecord; log: HilEventLogRecord | null }) => {
    const envelope = hilInboxApiClient.mapToV13Envelope(item, eventLog);
    pushToEngine(envelope);
    pushLog(`Injected "${item.Title}" (InboxItem #${item.InboxItemID}, Session #${item.EngageSessionID})`);
  };

  const injectAllApiItems = () => {
    apiItems.forEach(row => injectApiItem(row));
    pushLog(`Injected all ${apiItems.length} items into engine`);
  };

  // ── Resolve / Custom JSON ───────────────────────────────────────────────────

  const resolveActive = () => {
    if (!active) return;
    const id = active.Session.EngageSessionID;
    engine.resolve(id);
    pushLog(`Resolved EngageSessionID=${id}`);
  };

  const injectCustomJson = () => {
    setJsonError(null);
    try {
      const parsed = JSON.parse(customJson) as HilPresentationRequestEnvelope;
      pushToEngine(parsed);
      pushLog('Injected custom JSON envelope');
    } catch (e) {
      setJsonError('Invalid JSON: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────

  const btn = (active: boolean, color = '#3b82f6'): React.CSSProperties => ({
    padding: '7px 14px',
    borderRadius: '5px',
    border: active ? 'none' : '1px solid var(--panel-value-border)',
    background: active ? color : 'var(--panel-value-bg)',
    color: active ? '#fff' : 'var(--text-primary)',
    fontWeight: 600,
    fontSize: '12px',
    cursor: active ? 'pointer' : 'not-allowed',
    width: '100%',
    boxShadow: active ? 'none' : 'var(--card-shadow)',
  });

  const inputStyle: React.CSSProperties = {
    padding: '6px 8px',
    borderRadius: '5px',
    border: '1px solid var(--panel-value-border)',
    fontSize: '12px',
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--panel-value-bg)',
    color: 'var(--panel-value-color)',
    boxShadow: 'var(--card-shadow)',
  };

  const textAreaStyle: React.CSSProperties = {
    ...inputStyle,
    fontFamily: 'monospace',
    resize: 'vertical',
  };

  const sectionLabel: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '8px',
  };

  // Scoped theme override — matches the navy-slate palette used by Observer Configuration
  // (e.g. #020617/#111827 body/panel) instead of the app's default (more olive/gray) panel
  // variables, without touching those shared variables anywhere else in the app.
  // Card/field treatment (--panel-value-bg/border): solid equivalents of Observer
  // Configuration's translucent "About card" style (rgba(255,255,255,0.05) fill /
  // rgba(58,140,69,0.2) border) flattened against #020617 — kept opaque (not rgba) so
  // fields/cards render identically regardless of which panel backdrop they sit on.
  //
  // Follows useUIStore's theme instead of being fixed to the dark variant: previously this
  // was hardcoded dark-only, so switching the app to light theme left this console as a jarring
  // dark island against an otherwise light UI. The light variant below is the same navy-slate
  // *structure* (bg-main lighter than panel-bg would be inverted for light — see relative
  // lightness swap) translated to light equivalents from variables.css's [data-theme="light"] block.
  const darkThemeVars = {
    '--bg-main': '#020617',
    '--panel-bg': '#111827',
    '--panel-border': '#374151',
    '--panel-value-bg': '#0f1223',
    '--panel-value-border': '#0d2120',
    '--panel-value-color': '#f9fafb',
    '--text-primary': '#f9fafb',
    '--text-secondary': '#9ca3af',
    '--toolbar-bg': '#020617',
    '--card-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
  };

  const lightThemeVars = {
    '--bg-main': '#f8fafc',
    '--panel-bg': '#ffffff',
    '--panel-border': '#e5e7eb',
    '--panel-value-bg': '#f9fafb',
    '--panel-value-border': '#e5e7eb',
    '--panel-value-color': '#101211',
    '--text-primary': '#101211',
    '--text-secondary': '#6b7879',
    '--toolbar-bg': '#ffffff',
    '--card-shadow': '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  };

  const themeVars = (theme === 'light' ? lightThemeVars : darkThemeVars) as React.CSSProperties;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="hil-test-page" style={{ ...themeVars, fontFamily: 'system-ui, sans-serif', background: 'var(--bg-main)' }}>

      {/* ── Left panel ── */}
      <div className="hil-test-left-panel" style={{ display: 'flex', flexDirection: 'column', background: 'var(--panel-bg)', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--panel-border)', background: 'var(--toolbar-bg)' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>HIL v1.3 Test Console</div>
          <div style={{ fontSize: '11px', color: 'var(--text-primary)', marginTop: '2px' }}>
            Load pending items from backend API → inject into overlay
          </div>
        </div>

        {/* Queue status */}
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--panel-border)', background: 'var(--bg-main)', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
            Queue: <strong style={{ color: count > 0 ? '#4ade80' : 'var(--text-primary)' }}>{count}</strong>
          </div>
          {active && (
            <div style={{ fontSize: '11px', color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Active: {active.Title ?? active.HilType}
            </div>
          )}
          {pending.length > 0 && (
            <div style={{ fontSize: '11px', color: '#d69e2e' }}>{pending.length} pending</div>
          )}
          <button
            className="hil-btn"
            onClick={resolveActive}
            disabled={!active}
            style={{
              padding: '4px 10px', borderRadius: '4px', border: '1px solid var(--panel-value-border)',
              background: active ? 'var(--panel-value-bg)' : 'var(--panel-bg)', color: active ? 'var(--text-secondary)' : 'var(--text-primary)',
              fontSize: '11px', cursor: active ? 'pointer' : 'not-allowed',
            }}
          >
            Resolve Active
          </button>
        </div>

        {/* ── Load from API ── */}
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--panel-border)', background: 'var(--bg-main)' }}>
          <div style={sectionLabel}>Load from API</div>

          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '11px', color: 'var(--text-primary)', display: 'block', marginBottom: '4px' }}>
              Actor ID
            </label>
            <input
              className="hil-input"
              value={actorID}
              onChange={e => { setActorID(e.target.value); setApiError(null); setApiLoaded(false); }}
              placeholder='e.g. user:1 or admin'
              style={inputStyle}
              onKeyDown={e => e.key === 'Enter' && loadFromApi()}
            />
            <div style={{ fontSize: '10px', color: 'var(--text-primary)', marginTop: '3px' }}>
              Matches AssignedToActorID in ProcessEngage_InboxItems
            </div>
          </div>

          <button
            className="hil-btn"
            onClick={loadFromApi}
            disabled={loadingApi || !actorID.trim()}
            style={btn(!loadingApi && !!actorID.trim(), '#16a34a')}
          >
            {loadingApi ? 'Loading…' : 'Load Pending Items'}
          </button>

          {apiError && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#f87171', background: 'var(--error-bg)', padding: '6px 8px', borderRadius: '4px' }}>
              {apiError}
            </div>
          )}

          {/* Loaded items */}
          {apiItems.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-primary)' }}>
                  {apiItems.length} item{apiItems.length !== 1 ? 's' : ''} found
                </div>
                <button
                  className="hil-btn"
                  onClick={injectAllApiItems}
                  style={{
                    padding: '3px 8px', borderRadius: '4px', border: '1px solid var(--panel-value-border)',
                    background: 'var(--toolbar-bg)', color: '#93c5fd', fontSize: '10px', cursor: 'pointer',
                  }}
                >
                  Inject All
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {apiItems.map(({ item, log: eventLog }) => {
                  const badge = badgeForActionType(item.ActionType);
                  return (
                    <button
                      key={item.InboxItemID}
                      className="hil-btn"
                      onClick={() => injectApiItem({ item, log: eventLog })}
                      style={{
                        padding: '8px 10px', borderRadius: '5px', border: '1px solid var(--panel-value-border)',
                        background: 'var(--panel-value-bg)', fontSize: '12px', textAlign: 'left',
                        cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '3px',
                        boxShadow: 'var(--card-shadow)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <span style={{
                          padding: '1px 6px', borderRadius: '99px',
                          background: BADGE_COLORS[badge] ?? '#6b7280',
                          color: '#fff', fontSize: '10px', fontWeight: 700,
                          flexShrink: 0, textTransform: 'uppercase',
                        }}>
                          {badge}
                        </span>
                        <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.Title}
                        </span>
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-primary)', display: 'flex', gap: '8px' }}>
                        <span>Session #{item.EngageSessionID}</span>
                        {item.WorkflowName && <span>· {item.WorkflowName}</span>}
                        {eventLog?.PrimaryFormID && <span>· Form #{eventLog.PrimaryFormID}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {apiLoaded && apiItems.length === 0 && !apiError && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-primary)', textAlign: 'center', padding: '8px' }}>
              No pending items for this actor.
            </div>
          )}
        </div>

        {/* ── Custom JSON ── */}
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--panel-border)', flex: 1, background: 'var(--bg-main)' }}>
          <div style={sectionLabel}>Custom Envelope JSON</div>
          <textarea
            className="hil-input"
            value={customJson}
            onChange={e => { setCustomJson(e.target.value); setJsonError(null); }}
            placeholder={'{\n  "Version": "1.3",\n  "Session": { ... },\n  ...\n}'}
            rows={8}
            style={textAreaStyle}
          />
          {jsonError && (
            <div style={{ fontSize: '11px', color: '#f87171', marginTop: '4px' }}>{jsonError}</div>
          )}
          <button
            className="hil-btn"
            onClick={injectCustomJson}
            disabled={!customJson.trim()}
            style={{ ...btn(!!customJson.trim(), '#16a34a'), marginTop: '8px' }}
          >
            Inject Custom JSON
          </button>
        </div>

        {/* ── Log ── */}
        {log.length > 0 && (
          <div style={{ padding: '10px 14px', flexShrink: 0 }}>
            <div style={sectionLabel}>Log</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {log.map((entry, i) => (
                <div key={i} style={{ fontSize: '10px', color: 'var(--text-primary)', fontFamily: 'monospace', lineHeight: 1.5, wordBreak: 'break-all' }}>
                  {entry}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Right panel ── */}
      <div className="hil-test-right-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', background: 'var(--bg-main)' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-primary)', width: '100%', maxWidth: '500px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px', color: 'var(--text-primary)' }}>
            <InboxIcon size={36} />
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>
            HIL Overlay — Live from Backend
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.6 }}>
            Enter your <strong style={{ color: 'var(--text-primary)' }}>Actor ID</strong> and click{' '}
            <strong style={{ color: actorID.trim() ? '#16a34a' : '#2563eb' }}>Load Pending Items</strong> to fetch real HIL tasks
            from the database. Click an item to inject it into the overlay.
          </div>

          <div style={{ marginTop: '16px', padding: '14px 16px', background: 'var(--panel-value-bg)', border: '1px solid var(--panel-value-border)', borderRadius: '8px', textAlign: 'left', width: '100%', boxSizing: 'border-box', boxShadow: 'var(--card-shadow)' }}>
            <div style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '10px', fontWeight: 600 }}>HOW IT WORKS</div>
            {[
              'POST /api/v1/ai/inbox-items/pending → get pending items for actor',
              'POST /api/v1/process/hil-event-logs/by-engage-session → get form ID + metadata',
              'Maps InboxItem + EventLog → v1.3 HilPresentationRequestEnvelope',
              'pushToEngine() → HilOverlay renders bottom-right',
              'On action → InboxItemHilSubmitClient → POST /inbox-items/{id}/act',
            ].map((item, i) => (
              <div key={i} style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', gap: '8px', marginBottom: '7px' }}>
                <span style={{ color: '#2563eb', flexShrink: 0 }}>{i + 1}.</span>
                <span style={{ fontFamily: item.startsWith('POST') ? 'monospace' : 'inherit', fontSize: item.startsWith('POST') ? '12px' : '13px' }}>{item}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '12px', padding: '10px 16px', background: 'var(--panel-value-bg)', border: '1px solid var(--panel-value-border)', borderRadius: '8px', textAlign: 'left', width: '100%', boxSizing: 'border-box', boxShadow: 'var(--card-shadow)' }}>
            <div style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: 600 }}>ACTOR ID HINT</div>
            <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.6 }}>
              Format set by backend: <code style={{ background: 'var(--bg-main)', padding: '2px 5px', borderRadius: '3px', color: '#a5b4fc', fontSize: '12px' }}>user:{'{userId}'}</code><br />
              Override: <code style={{ background: 'var(--bg-main)', padding: '2px 5px', borderRadius: '3px', color: '#a5b4fc', fontSize: '12px' }}>localStorage.setItem('hilActorID', 'user:1')</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
